import { useState, useMemo } from 'react';
import { DataTable, DataTableSortEvent, DataTableFilterMeta } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import { Message } from 'primereact/message';
import { Button } from 'primereact/button';
import { LedgerEntry } from '../../types';
import { usePersistentState } from '../../hooks';
import { formatCurrency, formatDate } from '../../utils';

interface UnallocatedLedgerPanelProps {
  entries: LedgerEntry[];
  loading: boolean;
  selectionMode?: 'single' | null;
  selectedEntryId?: string | null;
  onSelectionChange?: (entryId: string | null) => void;
  onFilterByAmount?: (amount: number) => void;
  title?: string;
  showCheckNo?: boolean;
}

export default function UnallocatedLedgerPanel({
  entries,
  loading,
  selectionMode,
  selectedEntryId,
  onSelectionChange,
  onFilterByAmount,
  title = 'Ledger Entries',
  showCheckNo = false,
}: UnallocatedLedgerPanelProps) {
  const [error] = useState<string | null>(null);
  const [sortState, setSortState] = usePersistentState<{
    sortField: string;
    sortOrder: 1 | 0 | -1 | null | undefined;
  }>('reconciliation:unallocatedLedger:sort', {
    sortField: 'entry_date',
    sortOrder: 1,
  });
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    entry_date: { value: null, matchMode: FilterMatchMode.CONTAINS },
    amount: { value: null, matchMode: FilterMatchMode.CONTAINS },
    check_no: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const dateTemplate = (rowData: LedgerEntry) => {
    return formatDate(rowData.entry_date);
  };

  const amountTemplate = (rowData: LedgerEntry) => {
    return (
      <div className="amount-cell">
        <span>{formatCurrency(rowData.amount)}</span>
        {onFilterByAmount && (
          <Button
            className="filter-btn"
            icon="pi pi-filter"
            size="small"
            severity="secondary"
            text
            tooltip="Filter by this amount"
            tooltipOptions={{ position: 'top' }}
            onClick={(e) => {
              e.stopPropagation();
              onFilterByAmount(Math.abs(rowData.amount));
            }}
          />
        )}
      </div>
    );
  };

  const handleRowClick = (e: any) => {
    if (selectionMode === 'single' && onSelectionChange) {
      const clickedId = e.data.id;
      // Toggle selection: if already selected, deselect; otherwise select
      onSelectionChange(selectedEntryId === clickedId ? null : clickedId);
    }
  };

  const getSelectedEntry = () => {
    if (selectionMode === 'single' && selectedEntryId) {
      return entries.find((e) => e.id === selectedEntryId) || null;
    }
    return null;
  };

  const handleSort = (event: DataTableSortEvent) => {
    setSortState({
      sortField: event.sortField ?? sortState.sortField,
      sortOrder: event.sortOrder ?? sortState.sortOrder,
    });
  };

  const dateRange = useMemo(() => {
    const validDates = entries
      .map((e) => e.entry_date)
      .filter((date): date is string => date !== null && date !== undefined)
      .map((date) => new Date(date))
      .filter((date) => !isNaN(date.getTime()));

    if (validDates.length === 0) return null;

    const minDate = new Date(Math.min(...validDates.map((d) => d.getTime())));
    const maxDate = new Date(Math.max(...validDates.map((d) => d.getTime())));

    return {
      min: minDate.toLocaleDateString(),
      max: maxDate.toLocaleDateString(),
    };
  }, [entries]);

  return (
    <>
      {error && <Message severity="error" text={error} />}

      {entries.length === 0 && !loading ? (
        <Message
          severity="info"
          text="No unallocated ledger entries found. All entries have been allocated to customers."
        />
      ) : (
        <>
          <h3>
            {title}
            {dateRange && (
              <span className="date-range">
                ({dateRange.min} - {dateRange.max})
              </span>
            )}
          </h3>
          <DataTable
            value={entries}
            dataKey="id"
            paginator
            scrollable
            scrollHeight="flex"
            sortField={sortState.sortField}
            sortOrder={sortState.sortOrder ?? undefined}
            rows={25}
            stripedRows
            rowsPerPageOptions={[25, 50, 100]}
            paginatorTemplate={{
              layout:
                'RowsPerPageDropdown FirstPageLink PrevPageLink NextPageLink LastPageLink CurrentPageReport',
              CurrentPageReport: (options) => (
                <span className="paginator-current">
                  Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> ledger entries
                </span>
              ),
            }}
            loading={loading}
            selectionMode={selectionMode === 'single' ? 'single' : undefined}
            selection={getSelectedEntry()}
            onSelectionChange={(e: any) => {
              if (selectionMode === 'single' && onSelectionChange) {
                const selected = e.value as LedgerEntry | null;
                onSelectionChange(selected ? selected.id : null);
              }
            }}
            onRowClick={handleRowClick}
            onSort={handleSort}
            filterDisplay="menu"
            filters={filters}
            onFilter={(e) => setFilters(e.filters)}
          >
            <Column field="entry_date" header="Date" style={{ width: showCheckNo ? '15%' : '18%' }} sortable body={dateTemplate} filter showFilterMatchModes={false} />
            <Column field="amount" header="Amount" style={{ width: showCheckNo ? '18%' : '25%' }} sortable body={amountTemplate} filter showFilterMatchModes={false} />
            {showCheckNo && <Column field="check_no" header="Chk #" style={{ width: '18%' }} sortable filter showFilterMatchModes={false} />}
            <Column field="description" header="Description" sortable filter showFilterMatchModes={false} />
          </DataTable>
        </>
      )}
    </>
  );
}
